// Заказ раунда для тестировщика, см. ADR-32.
//
// Тестировщик зовёт команду заказа руками из консоли браузера, см. game/console.ts. Команда
// заряжает следующий спин заказом и жмёт кнопку за игрока: раунд дальше идёт как обычный,
// со всеми своими анимациями.
//
// Решает всё бэкенд. Клиент называет заказ в запросе спина и показывает, что ему ответили:
// ни поля, ни бонуса он не выдумывает.

import { CHEATS, type Cheat } from '../api/spin';

/** Что каждый заказ обещает. Эти строки игрок читает в консоли. */
const WHAT: Readonly<Record<Cheat, string>> = {
  wild: 'wild на всех барабанах, мини-слот открывается целиком',
  multiplier: 'x10 на поле',
  steamSpin: 'бонус от манометра',
  redHotSpin: 'бонус от скаттеров',
};

/** Игра, какой её видит консоль. */
export interface CheatBoard {
  /** Заряжает следующий спин заказом. Null — снимает заряд. */
  arm(cheat: Cheat | null): void;

  /** Жмёт кнопку спина. */
  spin(): void;
}

/**
 * Заказывает один спин для проверки показа.
 *
 * И кнопка в настройках, и команда из консоли запускают один и тот же путь: заказ живёт
 * ровно до следующего запроса и не может случайно остаться на обычном спине.
 */
export function playCheat(game: CheatBoard, cheat: Cheat): void {
  game.arm(cheat);
  game.spin();
}

/** Команды заказа: по одной на каждый заказ, плюс общие. */
export type CheatCommands = Readonly<Record<Cheat, () => void>> & {
  /** Все заказы по именам. */
  readonly cheats: readonly Cheat[];

  /** Заряжает следующий спин заказом, не крутя барабаны. Null — снимает заряд. */
  arm(cheat: Cheat | null): void;

  /** Крутит барабаны обычным спином. */
  spin(): void;
};

/**
 * Список команд заказа для консоли браузера.
 *
 * @param name Имя объекта консоли в window: с него начинается каждая команда.
 */
export function cheatHelp(name: string): string {
  const lines: string[] = CHEATS.map((cheat: Cheat) => `  ${name}.${cheat}() — ${WHAT[cheat]}`);

  return [
    'Заказ раунда для проверки показа:',
    ...lines,
    `  ${name}.arm('${CHEATS[0]}') — зарядить заказ, не крутя барабаны`,
    `  ${name}.spin() — обычный спин`,
  ].join('\n');
}

/**
 * Собирает команды заказа.
 *
 * Команда заказа заряжает спин и тут же жмёт кнопку: заказ живёт один запрос, и держать
 * его между спинами незачем.
 */
export function cheatCommands(game: CheatBoard): CheatCommands {
  const commands = {} as Record<Cheat, () => void>;

  for (const cheat of CHEATS) {
    commands[cheat] = (): void => {
      playCheat(game, cheat);
    };
  }

  return {
    ...commands,
    cheats: CHEATS,
    arm: (cheat: Cheat | null): void => {
      game.arm(cheat);
    },
    spin: (): void => {
      game.arm(null);
      game.spin();
    },
  };
}
